/**
 * Cliente da API de turmas (`/api/v2`) — painel administrativo.
 *
 * Mesmas três regras de `acesso/api.ts` (login/dispositivos, `/api/v1`):
 * cookie de sessão nunca lido nem escrito por aqui; CSRF lido de um cookie
 * legível próprio (`nev_admin_csrf`, o mesmo da sessão administrativa — a
 * autenticação é uma só, só o prefixo da rota muda entre v1 e v2); prova de
 * reautenticação só em memória.
 */

const BASE = '/api/v2';
const CSRF_COOKIE_NAME = 'nev_admin_csrf';

function readCsrfCookie(): string | undefined {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match?.slice(CSRF_COOKIE_NAME.length + 1);
}

export interface ApiProblem {
  readonly status: number;
  readonly code: string;
  readonly title: string;
}

export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: ApiProblem };

async function request<T>(
  path: string,
  init: RequestInit & {
    withCsrf?: boolean;
    idempotencyKey?: string;
    reauthToken?: string;
    ifMatch?: number;
  } = {},
): Promise<ApiResult<T>> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (init.withCsrf === true) {
    const csrfToken = readCsrfCookie();
    if (csrfToken !== undefined) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }
  if (init.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', init.idempotencyKey);
  }
  if (init.reauthToken !== undefined) {
    headers.set('X-Reauthentication-Token', init.reauthToken);
  }
  if (init.ifMatch !== undefined) headers.set('If-Match', `"${init.ifMatch}"`);

  let response: Response;
  try {
    response = await fetch(path.startsWith('/api/') ? path : `${BASE}${path}`, {
      ...init,
      headers,
      credentials: 'same-origin',
    });
  } catch {
    return {
      ok: false,
      problem: { status: 0, code: 'NETWORK_ERROR', title: 'Não foi possível conectar ao serviço.' },
    };
  }

  if (response.status === 204) {
    return { ok: true, value: undefined as T };
  }

  // Contrato v2.1: corpo de erro só `{code, title, correlation_id}`, sem
  // `fields` — mesma decisão já registrada do lado da participante.
  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const problem = (body ?? {}) as { code?: string; title?: string };
    return {
      ok: false,
      problem: {
        status: response.status,
        code: problem.code ?? 'UNKNOWN',
        title: problem.title ?? 'Não foi possível concluir a operação.',
      },
    };
  }

  return { ok: true, value: body as T };
}

export interface CohortSummary {
  readonly cohort_id: string;
  readonly code: string;
  readonly name: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly status: string;
  readonly accepting_new: boolean;
  readonly participant_count: number;
  readonly enrollment_url: string | null;
  readonly revision: number;
}

export interface CohortDetail extends CohortSummary {
  readonly release_binding_id: string | null;
  readonly release_binding_status: string | null;
  readonly release_binding_ui_content_version: string | null;
}

export interface CohortParticipant {
  readonly participation_id: string;
  readonly journey_state: string;
  readonly eligibility_state: string;
  readonly legal_acceptance_state: string;
  /** `null` = jornada ainda não iniciada (decisão de 12/09/2026). */
  readonly access_deadline: string | null;
  readonly created_at: string;
}

export interface FinalParticipationStatus {
  readonly participation_id: string;
  readonly final_state: string;
  readonly revision: number;
  readonly draft_revision: number;
  readonly access_deadline: string;
}

export interface InitialCorrectionStatus {
  readonly participation_id: string;
  readonly revision: number;
  readonly initial_state: string;
  readonly eligibility_state: string;
  readonly has_final_result: boolean;
  readonly correction_case_id: string | null;
  readonly exceptional_comparison_available: boolean;
  readonly draft_expires_at?: string | null;
  readonly renewal_available?: boolean;
}

export interface ExceptionalComparisonAccepted {
  readonly accepted: true;
  readonly document_generated: false;
  readonly comparison: {
    readonly global: { readonly displayText: string; readonly classification: string };
    readonly pillars: readonly { readonly code: string; readonly classification: string }[];
  };
}

export type CohortAction =
  | 'activate'
  | 'suspend'
  | 'reactivate'
  | 'open-new-participations'
  | 'close-new-participations'
  | 'rotate-link';

export const turmasApi = {
  initialCorrectionStatus(participationId: string): Promise<ApiResult<InitialCorrectionStatus>> {
    return request(
      `/admin/participations/${encodeURIComponent(participationId)}/initial-correction-status`,
      { method: 'GET' },
    );
  },

  reopenInitial(input: {
    participationId: string;
    revision: number;
    reason: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<
    ApiResult<{
      participation_id: string;
      initial_state: 'reopened';
      revision: number;
      draft_revision: number;
      correction_case_id: string;
    }>
  > {
    return request(
      `/admin/participations/${encodeURIComponent(input.participationId)}/initial-reopen`,
      {
        method: 'POST',
        withCsrf: true,
        ifMatch: input.revision,
        reauthToken: input.reauthToken,
        idempotencyKey: input.idempotencyKey,
        body: JSON.stringify({ reason: input.reason, identity_confirmed: true }),
      },
    );
  },

  acceptExceptionalComparison(input: {
    participationId: string;
    revision: number;
    correctionCaseId: string;
    reason: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<ExceptionalComparisonAccepted>> {
    return request(
      `/admin/participations/${encodeURIComponent(input.participationId)}/exceptional-comparison`,
      {
        method: 'POST',
        withCsrf: true,
        ifMatch: input.revision,
        reauthToken: input.reauthToken,
        idempotencyKey: input.idempotencyKey,
        body: JSON.stringify({
          accepted: true,
          reason: input.reason,
          correction_case_id: input.correctionCaseId,
        }),
      },
    );
  },

  finalStatus(participationId: string): Promise<ApiResult<FinalParticipationStatus>> {
    return request(`/admin/participations/${encodeURIComponent(participationId)}/final-status`, {
      method: 'GET',
    });
  },

  reopenFinal(input: {
    participationId: string;
    revision: number;
    reason: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<
    ApiResult<{
      participation_id: string;
      final_state: 'reopened';
      revision: number;
      draft_revision: number;
    }>
  > {
    return request(
      `/admin/participations/${encodeURIComponent(input.participationId)}/reopen-final`,
      {
        method: 'POST',
        withCsrf: true,
        ifMatch: input.revision,
        reauthToken: input.reauthToken,
        idempotencyKey: input.idempotencyKey,
        body: JSON.stringify({ reason: input.reason, identity_confirmed: true }),
      },
    );
  },
  create(input: {
    code: string;
    name: string;
    startDate: string;
    endDate: string;
    idempotencyKey: string;
  }): Promise<
    ApiResult<{
      cohort_id: string;
      status: string;
      enrollment_url: string;
      accepting_new: boolean;
    }>
  > {
    return request('/admin/cohorts', {
      method: 'POST',
      withCsrf: true,
      idempotencyKey: input.idempotencyKey,
      body: JSON.stringify({
        code: input.code,
        name: input.name,
        start_date: input.startDate,
        end_date: input.endDate,
      }),
    });
  },

  list(): Promise<ApiResult<{ cohorts: readonly CohortSummary[] }>> {
    return request('/admin/cohorts', { method: 'GET' });
  },

  get(cohortId: string): Promise<ApiResult<CohortDetail>> {
    return request(`/admin/cohorts/${encodeURIComponent(cohortId)}`, { method: 'GET' });
  },

  update(input: {
    cohortId: string;
    revision: number;
    name: string;
    startDate: string;
    endDate: string;
  }): Promise<ApiResult<CohortSummary>> {
    return request(`/admin/cohorts/${encodeURIComponent(input.cohortId)}`, {
      method: 'PATCH',
      withCsrf: true,
      headers: { 'If-Match': `"${String(input.revision)}"` },
      body: JSON.stringify({
        name: input.name,
        start_date: input.startDate,
        end_date: input.endDate,
      }),
    });
  },

  executeAction(input: {
    cohortId: string;
    action: CohortAction;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<{ cohort_id: string; status: string }>> {
    return request(`/admin/cohorts/${encodeURIComponent(input.cohortId)}/actions/${input.action}`, {
      method: 'POST',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
    });
  },

  listParticipants(
    cohortId: string,
  ): Promise<ApiResult<{ participants: readonly CohortParticipant[] }>> {
    return request(`/admin/cohorts/${encodeURIComponent(cohortId)}/participants`, {
      method: 'GET',
    });
  },
};
